import * as Logger from './logger.js'
import { HSCard } from './card.js';
import { UserSession } from './session.js';
import * as Utils from './utils.js';
import styles from './table.css';
import template from './table.html';
import openLinkImg from './img/open-link.svg';
import { SlickGrid, SlickDataView, Formatters, SlickRowSelectionModel } from 'slickgrid';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Report engines emit this string in delta_* columns when the compare baseline is missing; downstream sums/pcts must skip these values.
export const MISSING_BASELINE_SENTINEL = '__missing_baseline__';

const isAssignedDeviceRow = (dataContext) => {
    const humaId = dataContext?.huma_id;
    return (typeof humaId === 'string') && UUID_REGEX.test(humaId.trim());
};

const getOnlineStateFromRow = (dataContext = {}) => {
    const rowRef = {
        deviceid: dataContext?.deviceid,
        huma_id: dataContext?.huma_id,
        providerid: dataContext?.providerid,
        displayname: dataContext?.displayname,
    };

    const parseTime = (value, fieldName) => {
        if (value === undefined || value === null || value === '') { return null; }

        let millis = null;
        if (typeof value === 'number') {
            millis = value;
        } else if (typeof value === 'string') {
            const str = value.trim();
            if (str.length === 0) { return null; }
            if (/^-?\d+(\.\d+)?$/.test(str)) {
                millis = Number(str);
            } else {
                millis = new Date(str).getTime();
            }
        } else {
            millis = new Date(value).getTime();
        }

        if (!Number.isFinite(millis)) {
            Logger.warn('Invalid online status timestamp: unparseable value', {
                field: fieldName,
                value,
                row: rowRef,
            });
            return null;
        }

        // Normalize epoch seconds to milliseconds for mixed-source data.
        if (Math.abs(millis) > 0 && Math.abs(millis) < 1000000000000) {
            Logger.warn('Online status timestamp appears to be epoch-seconds; normalizing to milliseconds', {
                field: fieldName,
                value,
                normalizedMillis: millis * 1000,
                row: rowRef,
            });
            millis *= 1000;
        }

        const now = Date.now();
        const minSupportedMillis = Date.UTC(2000, 0, 1);
        const maxSupportedMillis = now + (7 * 24 * 60 * 60 * 1000);
        if (millis < minSupportedMillis || millis > maxSupportedMillis) {
            Logger.warn('Invalid online status timestamp: value is outside supported range', {
                field: fieldName,
                value,
                parsedMillis: millis,
                minSupportedMillis,
                maxSupportedMillis,
                row: rowRef,
            });
            return null;
        }

        return millis;
    };

    const connectedAt = parseTime(dataContext?.lastconnecttime, 'lastconnecttime');
    const disconnectedAt = parseTime(dataContext?.lastdisconnecttime, 'lastdisconnecttime');

    if (connectedAt === null && disconnectedAt === null) {
        return { online: false, referenceTime: null };
    }
    if (connectedAt !== null && disconnectedAt === null) {
        return { online: true, referenceTime: connectedAt };
    }
    if (connectedAt === null && disconnectedAt !== null) {
        return { online: false, referenceTime: disconnectedAt };
    }
    if (connectedAt > disconnectedAt) {
        return { online: true, referenceTime: connectedAt };
    }

    return { online: false, referenceTime: disconnectedAt };
};


export class HSTable extends HSCard {
    dataView;
    table;
    searchStr = "";
    searchFilterExpr = null;   // config-driven search filter from workflow
    clicked = null;
    clickedRow = null;
    clickedCol = null;
    selectedRows = [];
    preHeaderGroups = [];
    preHeaderHeight = 30;

    constructor() {
        super();
        this.dataView = new SlickDataView();
        // NOTE: enabling inlineFilters may improve performance, but the callback cannot have external references
        //this.dataView = new SlickDataView({ inlineFilters: true });
    }

    onLoad() {
        this.showLoading();
        this.loadTemplate(template);
        this.loadStyles(styles);
    }

    init(config) {
        super.init(config);
        // if workflow config has a search filter expression, compile it
        if (config.search?.filter) {
            try {
                this.searchFilterExpr = JSON.stringify(config.search.filter);
                // validate the expression is compilable
                new Function(['row', 'search'], 'return ' + this.searchFilterExpr + ';');
                Logger.debug("Using workflow search filter: ", this.searchFilterExpr);
            } catch (err) {
                Logger.error("Invalid workflow search filter expression: ", err, config.search.filter);
                this.searchFilterExpr = null;
            }
        }
        requestAnimationFrame(() => {
            if (!this.isConnected) {
                Logger.debug('Skipping SlickGrid init for detached table: ', this.config?.id);
                return;
            }
            const parentNode = this.shadowRoot.getElementById('table-target');
            if (!parentNode) {
                Logger.error("No parent node found for table");
                return;
            }
            this.table = new SlickGrid(parentNode, this.dataView, [], {
                shadowRoot: this.shadowRoot,
                enableCellNavigation: false,
                enableTextSelectionOnCells: true,
                enableColumnReorder: false,
                forceFitColumns: true,
                multiColumnSort: true,  // shift-click additional column headers to add sort col
                enableAutoSizeColumns: true,
                rowHeight: 32,
                createPreHeaderPanel: true,
                showPreHeaderPanel: false,
                preHeaderPanelHeight: this.preHeaderHeight,
            });

            // Forward horizontal wheel events from the header area to the viewport.
            // SlickGrid's header is a sibling of the viewport, so wheel events over
            // the header never reach the viewport's scroll handler natively.
            parentNode.addEventListener('wheel', (e) => {
                if (e.deltaX === 0) return;
                const viewport = parentNode.querySelector('.slick-viewport');
                if (!viewport || viewport.contains(e.target)) return;
                viewport.scrollLeft += e.deltaX;
            }, { passive: true });

            // register our search filter callback
            this.dataView.setFilter(row => {
                // if config-driven filter is set (from workflow), apply it
                if (this.searchFilterExpr) {
                    try {
                        const filterFunc = new Function(['row', 'search'], 'return ' + this.searchFilterExpr + ';');
                        if (!filterFunc(row, this.searchStr)) {
                            return false;
                        }
                    } catch (err) {
                        Logger.error("Error evaluating search filter expression: ", err, this.searchFilterExpr);
                        return true;
                    }
                }
                // fallback: generic string search if no config filter or searchStr is set
                if (this.searchStr?.length > 0) {
                    return Object.values(row).some(val => val && val.toString().toLowerCase().includes(this.searchStr.toLowerCase()));
                }
                return true;
            });

            this.dataView.onRowCountChanged.subscribe((ev, args) => {
                this.emit({ type: 'hs-data-count', count: args.current });
                this.table.updateRowCount();
                this.table.render();
            });

            // update existing data in rows after loading (ie new updates arrived)
            this.dataView.onRowsChanged.subscribe((ev, args) => {
                this.table.invalidateRows(args.rows);
                this.table.render();
            });

            if (this.config.onclick?.action == 'select') {
                //this.table.setSelectionModel(new SlickRowSelectionModel());   // single row select
                this.table.setSelectionModel(new SlickRowSelectionModel({ selectActiveRow: false }));   // multi row select
            }

            this.table.onSort.subscribe((e, args) => {
                const sortCols = args.sortCols ||
                    (args.sortCol ? [{ sortCol: args.sortCol, sortAsc: args.sortAsc }] : []);
                this.onSort(args.sortAsc, sortCols);
            });
            this.table.onClick.subscribe((e, args) => this.onClick(e, args.row, args.cell));
            if (this.table.onColumnsResized) {
                this.table.onColumnsResized.subscribe(() => this.renderPreHeaderGroups());
            }
            this.load();
        });
    }

    setPreHeaderGroups(groups = [], { height = 30 } = {}) {
        this.preHeaderGroups = Array.isArray(groups) ? groups : [];
        this.preHeaderHeight = Number.isFinite(Number(height)) ? Number(height) : 30;

        if (typeof this.table?.setOptions === 'function') {
            this.table.setOptions({ preHeaderPanelHeight: this.preHeaderHeight }, true, true, true);
        }

        this.renderPreHeaderGroups();
    }

    renderPreHeaderGroups() {
        if (!this.table || typeof this.table.getColumns !== 'function') {
            return;
        }

        const leftPanel = this.table.getPreHeaderPanelLeft?.() || this.table.getPreHeaderPanel?.();
        const rightPanel = this.table.getPreHeaderPanelRight?.();

        if (leftPanel) {
            leftPanel.innerHTML = '';
            leftPanel.style.position = 'relative';
            leftPanel.style.height = `${this.preHeaderHeight}px`;
        }
        if (rightPanel) {
            rightPanel.innerHTML = '';
            rightPanel.style.position = 'relative';
            rightPanel.style.height = `${this.preHeaderHeight}px`;
        }

        const hasGroups = Array.isArray(this.preHeaderGroups) && this.preHeaderGroups.length > 0;
        if (typeof this.table.setPreHeaderPanelVisibility === 'function') {
            this.table.setPreHeaderPanelVisibility(hasGroups);
        }
        if (!hasGroups) {
            return;
        }

        const columns = this.table.getColumns() || [];
        const frozenColumn = Number(this.table.getOptions?.()?.frozenColumn);
        const hasFrozen = Number.isFinite(frozenColumn) && frozenColumn >= 0 && frozenColumn < columns.length;

        const leftCols = hasFrozen ? columns.slice(0, frozenColumn + 1) : columns;
        const rightCols = hasFrozen ? columns.slice(frozenColumn + 1) : [];

        const renderPaneGroups = (panelEl, paneCols) => {
            if (!panelEl || !Array.isArray(paneCols) || paneCols.length < 1) {
                return;
            }

            for (const group of this.preHeaderGroups) {
                const requestedCols = Array.isArray(group?.columns) ? group.columns.map(x => String(x)) : [];
                if (requestedCols.length < 1) {
                    continue;
                }

                const indices = paneCols
                    .map((col, idx) => requestedCols.includes(String(col?.field || col?.id || '')) ? idx : -1)
                    .filter(idx => idx >= 0);
                if (indices.length < 1) {
                    continue;
                }

                const start = Math.min(...indices);
                const end = Math.max(...indices);
                const left = paneCols.slice(0, start).reduce((sum, col) => sum + Number(col?.width || 0), 0);
                const width = paneCols.slice(start, end + 1).reduce((sum, col) => sum + Number(col?.width || 0), 0);
                if (!Number.isFinite(width) || width <= 0) {
                    continue;
                }

                const cell = document.createElement('div');
                cell.className = `hs-preheader-group${group.className ? ` ${group.className}` : ''}`;
                cell.style.left = `${left}px`;
                cell.style.width = `${width}px`;
                if (group.align) {
                    cell.style.textAlign = group.align;
                }
                cell.textContent = String(group.label || '');
                if (group.tooltip) {
                    cell.title = String(group.tooltip);
                    cell.setAttribute('aria-label', String(group.tooltip));
                }
                panelEl.appendChild(cell);
            }
        };

        renderPaneGroups(leftPanel, leftCols);
        renderPaneGroups(rightPanel, rightCols);
    }

    search(str) {
        Logger.debug("Applying search filter: ", str);
        this.searchStr = str;
        this.dataView.refresh();
    }

    //
    // cell formatters
    //
    dayFormatter(row, cell, value, columnDef, dataContext) {
        return value + ' days';
    }
    jsonObjectFormatter(row, cell, value, columnDef, dataContext) {
        if (value && typeof value === 'string') {
            try {
                value = JSON.parse(value);
            } catch (err) {
                Logger.error("Error parsing JSON: ", err);
                return '';
            }
        }
        if (value && typeof value === 'object') {
            return value[columnDef.subfield];
        } else {
            return '';
        }
    }
    dateFormatter(row, cell, value, columnDef, dataContext) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) { 
            const locale = navigator.language || 'en-US';
            return d.toLocaleDateString();
        } else {
            return '';
        }
    }
    clickableFormatter(row, cell, value, columnDef, dataContext) {
        // handle data export
        if (row == -1 && cell == -1) { return value; }
        return "<span style='cursor:pointer;'>" + value + "</span>";
    }
    openLinkFormatter(row, cell, value, columnDef, dataContext) {
        const userinfo = UserSession.get('userinfo');
        const user_providers = UserSession.get('user_providers') || [];
        value = columnDef.linkText || value;
        if (userinfo?.org == dataContext?.orgname && user_providers.find(x => x.providerid == dataContext?.providerid)) {
            return "<div class=\"icon-link\"><img src=\"" + openLinkImg + "\" alt=\"Open Tab\"><span>" + value + "</span></div>";
        } else {
            return '';
        }
    }
    isValidUuid(value) {
        if (typeof value !== 'string') { return false; }
        const uuid = value.trim();
        return UUID_REGEX.test(uuid);
    }
    isAssignedDevice(dataContext) {
        return isAssignedDeviceRow(dataContext);
    }
    assignedFormatter(row, cell, value, columnDef, dataContext) {
        const isAssigned = isAssignedDeviceRow(dataContext);
        return isAssigned ? "Assigned" : "Unassigned";
    }
    assignedSorter(a, b, columnDef) {
        const aAssigned = isAssignedDeviceRow(a);
        const bAssigned = isAssignedDeviceRow(b);
        if (aAssigned == bAssigned) { return 0; }
        if (aAssigned && !bAssigned) { return -1; }
        return 1;
    }
    deployedFormatter(row, cell, value, columnDef, dataContext) {
        return value == 'NOT_DEPLOYED' ? "Not deployed" : "Deployed";
    }
    deployedSorter(a, b, columnDef) {
        const aDeployed = a[columnDef.field] == 'NOT_DEPLOYED' ? false : true;
        const bDeployed = b[columnDef.field] == 'NOT_DEPLOYED' ? false : true;
        if (aDeployed == bDeployed) { return 0; }
        if (aDeployed && !bDeployed) { return -1; }
        return 1;
    }
    getOnlineState(dataContext) {
        return getOnlineStateFromRow(dataContext);
    }
    onlineFormatter(row, cell, value, columnDef, dataContext) {
        const state = getOnlineStateFromRow(dataContext);
        const online = state.online;
        const since = state.referenceTime === null ? NaN : Date.now() - state.referenceTime;
        if (!isNaN(since) && since >= 0) {
            const duration = {
                days: Math.floor(since / 86400000),
                hours: since > 86400000 ? 0 : Math.floor((since % 86400000) / 3600000),
                minutes: since > 3600000 ? 0 : Math.floor((since % 3600000) / 60000),
                seconds: since > 60000 ? 0 : Math.floor((since % 60000) / 1000)
            };
            const formatter = new Intl.DurationFormat(navigator.language, { style: "long" });
            const tooltip = (online ? "Online for " : "Offline for ") + formatter.format(duration);
            // handle data export
            if (row == -1 && cell == -1) { return tooltip; }
            return "<span title='" + tooltip + "'>" + (online ? "Online" : "Offline") + "</span>";
        } else {
            return (online ? "Online" : "Offline");
        }
    }
    onlineSorter(a, b) {
        const aOnline = getOnlineStateFromRow(a).online;
        const bOnline = getOnlineStateFromRow(b).online;
        if (aOnline == bOnline) { return 0; }
        if (aOnline && !bOnline) { return -1; }
        return 1;
    }
    connectedFormatter(row, cell, value, columnDef, dataContext) {
        if (value === 'true' || value === 'True' || value === 'TRUE' || value === true || value === 'Connected' || value === 'CONNECTED') { return 'Connected'; }
        if (value === undefined || value === null) { return ''; }
        return 'Disconnected';
    }
    connectedSorter(a, b, columnDef) {
        a = a[columnDef.field];
        b = b[columnDef.field];
        if (a === 'true' || a === 'True' || a === 'TRUE' || a === true ||
            a === 'Connected' || a === 'CONNECTED') {
            a = true;
        } else if (a !== undefined && a !== null) {
            a = false;
        }
        if (b === 'true' || b === 'True' || b === 'TRUE' || b === true ||
            b === 'Connected' || b === 'CONNECTED') {
            b = true;
        } else if (b !== undefined && b !== null) {
            b = false;
        }
        if ((a === null || a === undefined) && b !== null && b !== undefined) { return 1; }
        if ((b === null || b === undefined) && a !== null && a !== undefined) { return -1; }
        if (a == b) { return 0; }
        if (a && !b) { return -1; }
        return 1;
    }
    pairedFormatter(row, cell, value, columnDef, dataContext) {
        if (value === 'paired' || value === 'Paired' || value === 'PAIRED' || value == 'true' || value == 'True' || value === true) { return 'Paired'; }
        if (value === undefined || value === null) { return ''; }
        return 'Unpaired';
    }
    providerNameFormatter(row, cell, value, columnDef, dataContext) {
        if (dataContext?._isTotalsRow) { return 'TOTAL'; }
        if (!value) { return ''; }
        const providers = UserSession.get('providers') || [];
        const provider = providers.find(p => p.providerid === value);
        if (provider) return provider.displayname || provider.name || value;
        const userProviders = UserSession.get('user_providers') || [];
        const up = userProviders.find(p => p.providerid === value);
        return up ? (up.displayname || up.name || value) : value;
    }
    durationFormatter(row, cell, value, columnDef, dataContext) {
        if (value == null || isNaN(value)) { return ''; }
        const totalMinutes = Math.round(Number(value) / 60000);
        if (totalMinutes < 1) return '< 1 min';
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        if (hours === 0) return `${mins} min${mins !== 1 ? 's' : ''}`;
        if (mins === 0) return `${hours} hr${hours !== 1 ? 's' : ''}`;
        return `${hours} hr${hours !== 1 ? 's' : ''}, ${mins} min${mins !== 1 ? 's' : ''}`;
    }
    orgNameFormatter(row, cell, value, columnDef, dataContext) {
        return dataContext?.orgdisplayname || dataContext?.orgname || value || '';
    }
    percentFormatter(row, cell, value, columnDef, dataContext) {
        if (value == null || isNaN(value)) { return ''; }
        return `${Math.round(Number(value))}%`;
    }
    channelFormatter(row, cell, value, columnDef, dataContext) {
        if (!value) { return ''; }
        return `<b>${value}</b>`;
    }
    progressBarFormatter(row, cell, value, columnDef, dataContext) {
        if (value == null || isNaN(value)) { return ''; }
        const percent = Math.min(100, Math.max(0, Number(value)));
        const color = percent > 66 ? 'var(--blue, #2e72b7)' : percent > 33 ? 'var(--warn, #eb6f4a)' : 'var(--grey3, #999)';
        return `<span style="display:inline-block;width:100%;height:14px;background:#eee;border-radius:3px;overflow:hidden">`
            + `<span style="display:block;width:${percent}%;height:100%;background:${color}"></span></span>`;
    }

    //
    // ops user detection and automatic org-column injection
    //
    isOpsUser() {
        const roles = UserSession.get('userinfo')?.realm_access?.roles || [];
        return roles.includes('ops') || roles.includes('ops-admin');
    }

    // Returns a column definition to prepend for the org name, or null when not applicable.
    // Skipped when: not an ops user, data has no org fields, or an org column already exists.
    getAutoOrgColumn(data, existingCols) {
        if (!this.isOpsUser()) { return null; }
        if (existingCols.some(col => col.id === 'hs_auto_org')) { return null; }
        const orgFields = new Set(['orgname', 'orgdisplayname']);
        if (existingCols.some(col => orgFields.has(col.field))) { return null; }
        if (!data.some(row => row.orgname || row.orgdisplayname)) { return null; }
        return {
            id: 'hs_auto_org',
            name: 'Org',
            field: 'orgdisplayname',
            formatter: this.orgNameFormatter.bind(this),
            sortable: true,
            alphaSort: true,
            width: 120,
        };
    }

    //
    // multi-column sorting function
    //
    isMissingSortValue(value) {
        return value === null || value === undefined;
    }

    toFiniteNumber(value) {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.length < 1) {
                return null;
            }
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    }

    compareColumnValues(value1, value2, alphaSort, alphaComparer) {
        const missing1 = this.isMissingSortValue(value1);
        const missing2 = this.isMissingSortValue(value2);

        // Keep missing values at the bottom regardless of sort direction.
        if (missing1 && !missing2) { return 1; }
        if (missing2 && !missing1) { return -1; }
        if (missing1 && missing2) { return 0; }

        if (alphaSort) {
            return alphaComparer.compare(String(value1), String(value2));
        }

        const n1 = this.toFiniteNumber(value1);
        const n2 = this.toFiniteNumber(value2);
        const validNum1 = n1 !== null;
        const validNum2 = n2 !== null;

        if (validNum1 && validNum2) {
            if (n1 === n2) { return 0; }
            return n1 > n2 ? 1 : -1;
        }

        // For numeric columns, treat non-numeric values as invalid and keep them below valid numbers.
        if (validNum1 && !validNum2) { return -1; }
        if (validNum2 && !validNum1) { return 1; }

        return alphaComparer.compare(String(value1), String(value2));
    }

    onSort(asc, cols) {
        if (!Array.isArray(cols) || cols.length < 1) {
            return;
        }
        Logger.debug("sorting: ", asc, cols);
        const alphaComparer = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        const strictAlphaComparer = new Intl.Collator(undefined, { sensitivity: 'base' });
        const comparer = (dataRow1, dataRow2) => {
            for (let i = 0, l = cols.length; i < l; i++) {
                if (cols[i].sortCol.sorter) {
                    const customResult = Number(cols[i].sortCol.sorter(dataRow1, dataRow2, cols[i].sortCol));
                    if (Number.isFinite(customResult) && customResult !== 0) {
                        return customResult * (cols[i].sortAsc ? 1 : -1);
                    }
                    continue;
                }
                const field = cols[i].sortCol.field;
                const sign = cols[i].sortAsc ? 1 : -1;
                const alphaSort = cols[i].sortCol.alphaSort;
                const value1 = dataRow1[field];
                const value2 = dataRow2[field];
                const result = this.compareColumnValues(value1, value2, alphaSort, alphaSort ? strictAlphaComparer : alphaComparer);
                if (result !== 0) {
                    const missing1 = this.isMissingSortValue(value1);
                    const missing2 = this.isMissingSortValue(value2);
                    if (missing1 || missing2) {
                        return result;
                    }
                    return result * sign;
                }
            }
            return 0;
        }
        this.dataView.sort(comparer, asc);
        this.table.invalidate();
        this.table.render();
    }

    //
    // handle clicks
    //
    onClick(ev, row, cell) {
        this.clicked = { row, cell };
        this.clickedRow = this.dataView.getItem(row);
        this.clickedCol = this.table.getColumns()[cell];
        this.clickedValue = this.clickedRow[this.clickedCol.field];
        Logger.debug("clicked: ", {
            clicked: this.clicked,
            clickedRow: this.clickedRow,
            clickedCol: this.clickedCol,
            clickedValue: this.clickedValue });
        // check for row-level onclick action
        if (this.config?.onclick?.action) {
            this.runAction(this.config.onclick);
        }
        // check for cell-level onclick action
        if (this.clickedCol?.onclick?.action) {
            this.runAction(this.clickedCol.onclick);
        }
        //this.emit({ type: 'hs-table-row-clicked', source: this, item });
        ev.preventDefault();
    }

    runAction(opts) {
        const context = {
            col:      this.clickedCol,
            row:      this.clickedRow,
            rows:     this.selectedRows.map(x => x.obj),
            selected: [ this.clickedRow ],
        };
        const action = Utils.evaluate(opts.action, Object.keys(context), Object.values(context));
        if (action === 'select') {
            // row selection is local to the table — toggle and track
            const index = this.selectedRows.findIndex(x => x.row === this.clicked.row);
            if (index >= 0) {
                this.selectedRows.splice(index, 1);
            } else {
                this.selectedRows.push({ row: this.clicked.row, obj: this.clickedRow });
            }
            this.table.setSelectedRows(this.selectedRows.map(x => x.row));
            return;
        }
        super.runAction(opts, context);
    }

    async load() {
        //
        // if the columns are defined, set them now
        //
        if (this.config.cols) {
            const cols = structuredClone(this.config.cols);
            this.table.setColumns(cols.map(col => { 
                if (col.formatter) { col.formatter = this[col.formatter] || Formatters[col.formatter]; }
                if (col.sorter) { col.sorter = this[col.sorter] || undefined; }
                return col;
            }));
            this.renderPreHeaderGroups();
        }

        this.reloadData();
    }

    updateData(update) {
        Logger.debug("table got an update from SSE: ", update);
        const id = update.id;
        if (id === undefined || id === null) { return; }
        const existing = this.dataView.getItemById(id);
        if (!update.obj) {
            // deletion: remove the row if it exists
            if (existing) {
                this.dataView.deleteItem(id);
            }
        } else if (existing) {
            // update existing row in place
            this.dataView.updateItem(id, { ...update.obj, id });
        } else {
            // new row
            this.dataView.addItem({ ...update.obj, id });
        }
    }

    /**
     * Generic data transform pipeline applied after fetching source data.
     * Supported transform types are configured via this.config.transform.
     *
     * "unpivot": Converts records with dynamic keys into rows.
     *   Config (this.config.transform):
     *     type: "unpivot"
     *     keyField: name for the unpivoted key column (e.g. "channel")
     *     valueField: name for the unpivoted value column (e.g. "plays")
     *     sort: sort specifier, e.g. "-plays" for descending by plays
     *     includeKeys: optional array — if provided, ONLY these keys are unpivoted (whitelist mode)
     *     excludeKeys: optional array — keys to exclude from unpivot (blacklist mode, used when includeKeys is absent)
     *       When neither is provided, all numeric-valued non-id keys are unpivoted.
     *     computed: array of computed column definitions:
     *       { field, func } where func is one of:
     *         "rank"         — row rank (1-based, after sort)
     *         "pct_of_total" — value / sum(all values) * 100
     *         "pct_of_max"   — value / max(all values) * 100
     *         "distribute"   — distribute a total from auxiliarySrc proportionally
     *           requires: totalField (field name from auxiliarySrc to sum)
     *     titleTemplate: optional, e.g. "${title} (${count} items)" — updates grid title
     */
    async applyTransform() {
        const t = this.config.transform;
        if (!t || typeof t !== 'object') { return []; }

        if (t.type === 'unpivot') {
            return this.transformUnpivot(t);
        }

        Logger.warn('unknown transform type: ', t.type);
        return [];
    }

    async transformUnpivot(t) {
        const includeKeys = t.includeKeys ? new Set(t.includeKeys) : null;
        const excludeKeys = new Set(t.excludeKeys || []);
        // always exclude 'id' — it's a row identifier, never a data dimension
        excludeKeys.add('id');

        const data = await this.getSourceData(this.config.src);

        // auto-detect keys to exclude: on the first record, any non-numeric value's
        // key is treated as a metadata/dimension field and excluded from the unpivot.
        // This avoids hardcoding rollup-specific key names while still filtering out
        // string-typed columns like providerid, stats_type, etc.
        if (!includeKeys && data.length > 0) {
            const sample = data[0];
            for (const [key, val] of Object.entries(sample)) {
                if (typeof val !== 'number') {
                    excludeKeys.add(key);
                }
            }
        }

        const shouldInclude = (key) => {
            if (includeKeys) { return includeKeys.has(key); }
            return !excludeKeys.has(key);
        };

        // aggregate values by dynamic key across all records
        const totals = {};
        for (const record of data) {
            for (const [key, val] of Object.entries(record)) {
                if (shouldInclude(key) && typeof val === 'number') {
                    totals[key] = (totals[key] || 0) + val;
                }
            }
        }

        // fetch auxiliary source data if any computed column needs it
        let auxTotal = 0;
        if (this.config.auxiliarySrc && t.computed?.some(c => c.func === 'distribute')) {
            const auxData = await this.getSourceData(this.config.auxiliarySrc);
            const distributeSpec = t.computed.find(c => c.func === 'distribute');
            const totalField = distributeSpec?.totalField;
            if (totalField) {
                for (const record of auxData) {
                    auxTotal += Number(record[totalField]) || 0;
                }
            }
        }

        const keyField = t.keyField || '_key';
        const valueField = t.valueField || '_value';

        const entries = Object.entries(totals);
        const sumAll = entries.reduce((s, [, v]) => s + v, 0);
        const maxVal = entries.length > 0 ? Math.max(...entries.map(([, v]) => v)) : 0;

        // sort
        if (t.sort) {
            const desc = t.sort.startsWith('-');
            const sortField = desc ? t.sort.slice(1) : t.sort;
            if (sortField === valueField) {
                entries.sort((a, b) => desc ? b[1] - a[1] : a[1] - b[1]);
            } else {
                entries.sort((a, b) => desc ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0]));
            }
        }

        // build rows
        const rows = entries.map(([key, value], idx) => {
            const row = { id: `_unpivot_${idx}`, [keyField]: key, [valueField]: value };

            // apply computed columns
            for (const comp of (t.computed || [])) {
                if (comp.func === 'rank') {
                    row[comp.field] = idx + 1;
                } else if (comp.func === 'pct_of_total') {
                    row[comp.field] = sumAll > 0 ? (value / sumAll * 100) : 0;
                } else if (comp.func === 'pct_of_max') {
                    row[comp.field] = maxVal > 0 ? (value / maxVal * 100) : 0;
                } else if (comp.func === 'distribute') {
                    row[comp.field] = sumAll > 0 ? (auxTotal * value / sumAll) : 0;
                }
            }

            return row;
        });

        // update title with count if template provided
        if (t.titleTemplate) {
            const titleEl = this.parentElement?.querySelector('.grid-title');
            if (titleEl) {
                titleEl.textContent = t.titleTemplate
                    .replace('${count}', rows.length)
                    .replace('${title}', this.config.title || '');
            }
        }

        return rows;
    }

    /**
     * Multi-source merge: fetches data from multiple source configs,
     * then joins results by a common key field (config.mergeBy).
     * Each source can optionally remap field names via source.fieldMap.
     *
     * Also supports config.computed: array of post-merge computed columns:
     *   { field, func, numerator, denominator }
     *   func: "ratio" — numerator / denominator
     *   func: "pct"   — Math.round(numerator / denominator * 100)
     */
    async loadMultiSource() {
        const mergeKey = this.config.mergeBy;
        if (!mergeKey) {
            Logger.warn('multi-source table requires mergeBy config');
            return [];
        }

        const merged = {};
        for (const srcConfig of this.config.sources) {
            const data = await this.getSourceData(srcConfig);
            const fieldMap = srcConfig.fieldMap || {};
            for (const row of data) {
                const key = row[mergeKey];
                if (!key) { continue; }
                if (!merged[key]) { merged[key] = { [mergeKey]: key, id: key }; }
                for (const [k, v] of Object.entries(row)) {
                    const mappedKey = fieldMap[k] || k;
                    if (mappedKey !== 'id') {
                        merged[key][mappedKey] = v;
                    }
                }
            }
        }

        const rows = Object.values(merged);

        // apply computed columns
        if (this.config.computed) {
            for (const row of rows) {
                for (const comp of this.config.computed) {
                    const num = Number(row[comp.numerator]) || 0;
                    const den = Number(row[comp.denominator]) || 0;
                    if (comp.func === 'ratio') {
                        row[comp.field] = den > 0 ? num / den : 0;
                    } else if (comp.func === 'pct') {
                        row[comp.field] = den > 0 ? Math.round(num / den * 100) : 0;
                    }
                }
            }
        }

        return rows;
    }

    async reloadData() {
        //
        // get the row data to display 
        //

        // clear the table immediately so stale data is not shown during fetch
        if (this.dataView) {
            this.dataView.setItems([]);
        }

        let json;
        if (this.config.transform) {
            json = await this.applyTransform();
        } else if (this.config.sources) {
            json = await this.loadMultiSource();
        } else {
            json = await this.getSourceData(this.config.src);
        }
        if (this.config.idField) {
            // if an idField is defined, ensure each row has a unique id
            if (this.config.idField == 'auto') {
                json.forEach((row, index) => { row.id = `id_${index}`; });
            } else {
                json.forEach((row, index) => { row.id = row[this.config.idField] ? row[this.config.idField] : `id_${index}`; });
            }
        }

        //
        // if the columns weren't defined in config, generate them automatically from the data
        //
        if (!this.config.cols) {
            // build set of keys to exclude from auto-generated columns
            const excludeKeys = new Set(this.config.excludeKeys || []);
            if (this.config.excludeSystemKeys) {
                for (const k of ['id', 'providerid', 'useruuid', 'deviceuuid', 'orgid', 'field', 'stats_type', 'utc_timestamp_inms', 'timestamp', 'ts']) {
                    excludeKeys.add(k);
                }
            }

            const cols = Object.values(json.reduce((accum,row) => {
                for (const [key,val] of Object.entries(row)) {
                    if (!accum[key] && !excludeKeys.has(key)) {
                        accum[key] = { id: key, name: key, field: key, sortable: true };
                    }
                }
                return accum;
            }, {}));

            // prepend any explicitly defined columns (e.g., provider with formatter)
            if (this.config.prependCols) {
                const prependCols = this.config.prependCols.map(col => {
                    if (col.formatter) col.formatter = this[col.formatter] || Formatters[col.formatter];
                    return col;
                });
                cols.unshift(...prependCols);
            }

            Logger.debug("auto cols: ", cols);
            this.table.setColumns(cols);
            this.renderPreHeaderGroups();
        }

        // auto-inject org column for ops users when data contains org fields
        const orgCol = this.getAutoOrgColumn(json, this.table.getColumns());
        if (orgCol) {
            this.table.setColumns([orgCol, ...this.table.getColumns()]);
            this.renderPreHeaderGroups();
        }

        if (this.config.showTotalsRow) {
            const totalsRow = this.computeTotalsRow(json, this.table.getColumns());
            json.unshift(totalsRow);
            this.installTotalsRowHooks();
        }

        this.dataView.setItems(json);
        this.emit({ type: 'hs-data-count', count: this.dataView.getLength() });

        // load the data in batches for better performance
        //this.dataView.beginUpdate();
        //this.dataView.addItem(data);
        //this.dataView.endUpdate();

        this.hideLoading();
        this.redraw();
    }

    useFakeData() {
        this.table.setColumns([
            { id: "sel", name: "#", field: "num", behavior: "select", cssClass: "cell-selection", width: 40, resizable: false, selectable: false },
            { id: "title", name: "Title", field: "title", cssClass: "cell-title", sortable: true, formatter: this.clickableFormatter },
            { id: "duration", name: "Duration", field: "duration", sortable: true, formatter: this.dayFormatter, filterable: true },
            { id: "%", name: "% Complete", field: "percentComplete", formatter: Formatters.PercentCompleteBar, sortable: true },
            { id: "start", name: "Formatted date", field: "start", minWidth: 60, formatter: this.dateFormatter, sortable: true },
            { id: "finish", name: "Plain date", field: "finish", minWidth: 60, sortable: true },
            { id: "checkmark", name: "Checkmark", minWidth: 20, cssClass: "cell-effort-driven", field: "effortDriven", formatter: Formatters.Checkmark, sortable: true },
            { id: "number", name: "Number", field: "number", sortable: true },
            { id: "string", name: "String", field: "string", sortable: true, alphaSort: true }
        ]);

        const data = [];
        for (let i = 0; i < 500; i++) {
            const startDate = new Date(new Date("1/1/1980").getTime() + Math.round(Math.random() * 365 * 25) * 86400000);
            const endDate = new Date(startDate.getTime() + Math.round(Math.random() * 365) * 86400000);
            data[i] = {
                num: i,
                id: "id_" + i,
                title: "Task " + i,
                path: "/some/path",
                duration: Math.round(Math.random() * 10) + 2,
                percentComplete: Math.round(Math.random() * 100),
                start: startDate,
                finish: endDate,
                checkmark: (i % 5 == 0),
                number: (i % 100 == 0) ? null : i,
                string: (i % 33 == 0) ? null : "string " + i,
            };
        }
        Logger.debug("setting fake data");
        this.dataView.setItems(data);
        /*
        const inputFilter = (item, args) => { if (item["duration"] < 5) { return false; } return true; }
        this.dataView.setFilter(inputFilter);
        */
    }

    redraw() {
        if (this.table) {
            this.table.resizeCanvas();
            this.table.invalidate();
        } else {
            Logger.warn("table not initialized");
        }
    }

    getExportData() {
        const cols = this.table.getColumns()
            .filter(col => {
                if (col.formatter) {
                    if ([
                            this.dayFormatter,
                            this.jsonObjectFormatter,
                            this.dateFormatter,
                            this.assignedFormatter,
                            this.deployedFormatter,
                            this.onlineFormatter,
                            this.connectedFormatter,
                            this.pairedFormatter,
                            this.clickableFormatter,
                            this.providerNameFormatter,
                            this.orgNameFormatter,
                            this.durationFormatter,
                            this.percentFormatter,
                            this.channelFormatter,
                            this.progressBarFormatter
                        ].includes(col.formatter)) {
                        // we can use these same formatters for the export data
                        return true;
                    }
                    // we don't export the columns for other formatters (openLinkFormatter, etc.) 
                    return false;
                }
                return true;
            })
            .map(col => {
                return ({ header: col.name, key: col.field, width: 30, context: col });
            });

        const data = this.dataView.getItems();
        Logger.debug("get data: ", data);
        const rows = data.map(row => {
            return cols.reduce((accum,col) => {
                if (col.context?.formatter) {
                    // we pass -1 for the cell and row so the formatter knows this is for export
                    accum[col.key] = col.context.formatter(-1, -1, row[col.key], col.context, row);
                } else {
                    accum[col.key] = row[col.key];
                }
                return accum; 
            }, {});
        });
        return { name: this.config.exportTitle, cols, rows };
    }

    // Builds a synthetic aggregate row summing every numeric field referenced by the columns; delta_*_pct is recomputed as a proper ratio of the aggregated totals when present.
    computeTotalsRow(rows, columnConfigs) {
        const totals = {
            _isTotalsRow: true,
            id: '__totals__',
            providerid: '__TOTAL__',
            displayname: 'TOTAL',
            providername: 'TOTAL',
        };
        if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(columnConfigs)) {
            return totals;
        }
        const skipFields = new Set(['providerid', 'displayname', 'providername', 'territory_id', 'orgid', 'orgname', 'id']);
        const fieldsToSum = new Set();
        for (const col of columnConfigs) {
            // Per-column opt-out: totalsOp === 'skip' leaves that column's cell blank on the totals row (used for avg/min/max/ratio columns where summing is meaningless).
            if (col?.totalsOp === 'skip') { continue; }
            if (col?.field && !skipFields.has(col.field)) {
                fieldsToSum.add(col.field);
            }
            if (col?.deltaField) fieldsToSum.add(col.deltaField);
        }
        for (const field of fieldsToSum) {
            let sum = 0;
            let hasAny = false;
            let allMissing = true;
            for (const row of rows) {
                const val = row?.[field];
                if (val === MISSING_BASELINE_SENTINEL) { continue; }
                if (val === undefined || val === null || val === '') { continue; }
                allMissing = false;
                const num = Number(val);
                if (Number.isFinite(num)) {
                    sum += num;
                    hasAny = true;
                }
            }
            totals[field] = hasAny ? sum : (allMissing ? MISSING_BASELINE_SENTINEL : 0);
        }
        for (const col of columnConfigs) {
            if (!col?.deltaField || !col?.deltaPctField || !col?.field) { continue; }
            const totalCurrent = totals[col.field];
            const totalDelta = totals[col.deltaField];
            if (totalCurrent === MISSING_BASELINE_SENTINEL || totalDelta === MISSING_BASELINE_SENTINEL) {
                totals[col.deltaPctField] = MISSING_BASELINE_SENTINEL;
                continue;
            }
            const curNum = Number(totalCurrent);
            const deltaNum = Number(totalDelta);
            if (!Number.isFinite(curNum) || !Number.isFinite(deltaNum)) {
                totals[col.deltaPctField] = MISSING_BASELINE_SENTINEL;
                continue;
            }
            const baseline = curNum - deltaNum;
            if (baseline === 0) {
                totals[col.deltaPctField] = MISSING_BASELINE_SENTINEL;
            } else {
                totals[col.deltaPctField] = (deltaNum / baseline) * 100;
            }
        }
        return totals;
    }

    // Registers dataView hooks that keep the totals row pinned at the top and unaffected by search/sort. Idempotent per table instance.
    installTotalsRowHooks() {
        if (this._totalsRowHooksInstalled) return;
        this._totalsRowHooksInstalled = true;

        const existingMetadataProvider = this.dataView.getItemMetadata?.bind(this.dataView);
        this.dataView.getItemMetadata = (row) => {
            const item = this.dataView.getItem(row);
            const baseMeta = existingMetadataProvider ? (existingMetadataProvider(row) || {}) : {};
            if (item?._isTotalsRow) {
                const existing = baseMeta.cssClasses ? String(baseMeta.cssClasses).trim() : '';
                baseMeta.cssClasses = existing ? `${existing} hs-totals-row` : 'hs-totals-row';
            }
            return baseMeta;
        };

        const originalSetFilter = this.dataView.setFilter.bind(this.dataView);
        this.dataView.setFilter = (filter) => {
            const wrapped = (row) => (row?._isTotalsRow ? true : filter(row));
            originalSetFilter(wrapped);
        };
        if (typeof this.dataView.reSort === 'function') {
            this.dataView.reSort();
        }

        const originalSort = this.dataView.sort.bind(this.dataView);
        this.dataView.sort = (comparer, ascending) => {
            const wrapped = (a, b) => {
                if (a?._isTotalsRow) return -1;
                if (b?._isTotalsRow) return 1;
                return comparer(a, b);
            };
            originalSort(wrapped, ascending);
        };
    }
}
customElements.define('hs-table', HSTable);

